import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface LeaderboardEntry {
  id?: number;
  user_id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  total_points: number;
  position?: number;
}

interface LeaderboardPreviewProps {
  tournamentId: string | number;
  entries: LeaderboardEntry[];
  currentUserId?: number;
  title?: string;
  subtitle?: string;
  maxItems?: number;
  className?: string;
}

const POSITION_COLORS = ['red', 'blue', 'green', 'orange'];

export const LeaderboardPreview: React.FC<LeaderboardPreviewProps> = ({
  tournamentId,
  entries,
  currentUserId,
  title,
  subtitle,
  maxItems = 4,
  className,
}) => {
  const { t } = useTranslation(['tournament', 'common']);
  const displayTitle = title || t('tournament:leaderboard');
  const displaySubtitle = subtitle || t('tournament:topParticipants');
  const displayEntries = entries.slice(0, maxItems);

  return (
    <Link
      to={`/tournaments/${tournamentId}/leaderboard`}
      className={`${styles.leaderboardPreview} ${className || ''}`}
    >
      {/* Header section */}
      <div className={styles.header}>
        <h4 className={styles.title}>{displayTitle}</h4>
        <p className={styles.subtitle}>{displaySubtitle}</p>
      </div>

      {/* Content section - separated with border */}
      <div className={styles.content}>
        <div className={styles.list}>
          {displayEntries.length > 0 ? (
            displayEntries.map((entry, index) => {
              const isCurrentUser = entry.id === currentUserId || entry.user_id === currentUserId;
              const fullName = `${entry.first_name || ''} ${entry.last_name || ''}`.trim();
              const displayName = fullName
                || entry.username
                || 'Unknown';

              return (
                <div
                  key={entry.id || entry.user_id || index}
                  className={`${styles.row} ${isCurrentUser ? styles.currentUser : ''}`}
                  data-color={POSITION_COLORS[index] || 'default'}
                >
                  <span className={styles.name}>{displayName}</span>
                  <span className={styles.points}>{entry.total_points}</span>
                </div>
              );
            })
          ) : (
            <div className={styles.empty}>{t('common:noDataYet')}</div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default LeaderboardPreview;
